import { useMemo, type FC, type ReactNode } from 'react';
import { CalendarClock, Check, CircleAlert, RotateCcw, X } from 'lucide-react';
import type { SampleTestDto } from '@/api';
import {
  Badge,
  Button,
  Table,
  Tooltip,
  cn,
  type TableColumn,
  type TableSort,
} from '@/ui';

import {
  formatDate,
  formatInstantDate,
  formatNumber,
  toNumber,
} from '../../lib/format';
import type { SampleTestOutcome } from './CloseSampleTestDialog';
import { SAMPLE_TEST_DISCREPANCIES, SAMPLE_TEST_STATUSES } from './lib/status';

export interface SampleTestsTableProps {
  tests: ReadonlyArray<SampleTestDto>;
  isLoading: boolean;
  sort: TableSort;
  onSortChange: (field: string) => void;
  emptyLabel: ReactNode;
  footer: ReactNode;
  onSchedule: (test: SampleTestDto) => void;
  onCloseWith: (test: SampleTestDto, outcome: SampleTestOutcome) => void;
  onReopen: (test: SampleTestDto) => void;
  /** Тест, который сейчас возвращается в тестирование, - его кнопка занята. */
  reopeningId: string | undefined;
}

/*
 * Таблица плана тестирования образцов - .ref-tbl «Проектной таблицы» макета, но
 * строки из отгрузок образцов 1С: без «Конечного продукта», добавления и
 * удаления. Действия - кнопками-иконками, как в макете: у теста в работе -
 * перенос и итог, у закрытого - возврат в тестирование.
 */
export const SampleTestsTable: FC<SampleTestsTableProps> = ({
  tests,
  isLoading,
  sort,
  onSortChange,
  emptyLabel,
  footer,
  onSchedule,
  onCloseWith,
  onReopen,
  reopeningId,
}) => {
  const columns = useMemo<ReadonlyArray<TableColumn<SampleTestDto>>>(
    () => [
      {
        id: 'partnerName',
        header: 'Клиент',
        isSortable: true,
        cell: (test) => (
          <span className="font-[550] text-ink-900">{test.partner.name}</span>
        ),
      },
      {
        id: 'productName',
        header: 'Товар',
        isSortable: true,
        cell: (test) => (
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
              <span className="text-[13px] text-ink-800">
                {test.product.code}
              </span>
              {test.product.groupName && (
                <Badge className="h-[18px] px-1.5 text-[10.5px]">
                  {test.product.groupName}
                </Badge>
              )}
            </div>
            <div className="text-[11.5px] leading-snug break-words text-ink-500">
              {test.product.name}
            </div>
          </div>
        ),
      },
      // Количество - товара, поэтому сразу за товаром, а не за отгрузкой.
      {
        id: 'quantity',
        header: 'Кол-во',
        align: 'right',
        width: 'w-[72px]',
        hideBelow: 'lg',
        cell: (test) => formatNumber(toNumber(test.quantity)),
      },
      {
        id: 'shipmentDate',
        header: 'Отгрузка',
        isSortable: true,
        // Шире номера на знак расхождения: номер в 1С - до 9 символов.
        width: 'w-[132px]',
        hideBelow: 'md',
        cell: (test) => (
          <div>
            <div className="flex items-center gap-1 text-xs text-ink-700 tabular-nums">
              <span>№ {test.shipment.number}</span>
              {test.onecDiscrepancies.length > 0 && (
                <OnecDiscrepancyMark test={test} />
              )}
            </div>
            <div className="text-[11.5px] text-ink-500 tabular-nums">
              {formatDate(test.shipment.date)}
            </div>
          </div>
        ),
      },
      {
        id: 'testDate',
        header: 'Дата теста',
        isSortable: true,
        width: 'w-[120px]',
        cell: (test) => <TestDateCell test={test} />,
      },
      {
        id: 'status',
        header: 'Статус',
        width: 'w-[120px]',
        // Бейдж и дата закрытия под ним - по центру друг под другом.
        align: 'center',
        cell: (test) => {
          const status = SAMPLE_TEST_STATUSES[test.status];

          return (
            <div className="flex flex-col items-center gap-0.5">
              <Badge tone={status.tone}>{status.label}</Badge>
              {test.closedAt && (
                <span className="text-[11px] text-ink-500">
                  {formatInstantDate(test.closedAt)}
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: 'comment',
        header: 'Комментарий',
        hideBelow: 'lg',
        cell: (test) =>
          test.comment ? (
            <span
              title={test.comment}
              className="line-clamp-3 text-xs text-ink-500"
            >
              {test.comment}
            </span>
          ) : (
            <span className="text-ink-400">-</span>
          ),
      },
      {
        id: 'actions',
        header: <span className="sr-only">Действия</span>,
        width: 'w-[104px]',
        align: 'right',
        cell: (test) => (
          <RowActions
            test={test}
            isReopening={reopeningId === test.id}
            onSchedule={onSchedule}
            onCloseWith={onCloseWith}
            onReopen={onReopen}
          />
        ),
      },
    ],
    [onSchedule, onCloseWith, onReopen, reopeningId],
  );

  return (
    <Table
      columns={columns}
      rows={tests}
      getRowKey={(test) => test.id}
      getRowAttributes={(test) => ({
        'data-row-id': test.id,
        'data-status': test.status,
      })}
      isLoading={isLoading}
      sort={sort}
      onSortChange={onSortChange}
      cellClassName="py-3"
      emptyLabel={emptyLabel}
      footer={footer}
    />
  );
};

/*
 * Дата теста: пора звонить - красным, как просроченная плановая дата задач;
 * перенесённая менеджером - с пометкой (за датой отгрузки больше не следует).
 */
const TestDateCell: FC<{ test: SampleTestDto }> = ({ test }) => (
  <div>
    <div
      className={cn(
        'text-[13px]',
        test.isDue ? 'font-[550] text-st-delayed' : 'text-ink-500',
      )}
    >
      {formatDate(test.testDate)}
    </div>
    {(test.isDue || test.isRescheduled) && (
      <div className="text-[11px] text-ink-500">
        {test.isDue && <span className="text-st-delayed">пора звонить</span>}
        {test.isDue && test.isRescheduled && ' · '}
        {test.isRescheduled && (
          <span title="Дату перенёс менеджер: за датой отгрузки она больше не следует">
            перенесена
          </span>
        )}
      </div>
    )}
  </div>
);

/*
 * Расхождение с 1С - красный знак у номера отгрузки, что случилось - в подсказке.
 * Подсказка в две короткие строки: обёртка таблицы обрезает выступающее, а у
 * последней строки под знаком только остаток строки и подвал.
 */
const OnecDiscrepancyMark: FC<{ test: SampleTestDto }> = ({ test }) => (
  <Tooltip
    label="Расхождение с 1С"
    className="shrink-0"
    content={
      <>
        {test.onecDiscrepancies.map((discrepancy) => (
          <div key={discrepancy} className="font-semibold">
            {SAMPLE_TEST_DISCREPANCIES[discrepancy]}
          </div>
        ))}
        <div className="opacity-80">
          Тест сохранён: у него есть отметки менеджера
        </div>
      </>
    }
  >
    <CircleAlert aria-hidden className="size-3.5 text-st-delayed" />
  </Tooltip>
);

const ICON_BUTTON = 'size-7 px-0 [&_svg]:size-[15px]';

const RowActions: FC<{
  test: SampleTestDto;
  isReopening: boolean;
  onSchedule: (test: SampleTestDto) => void;
  onCloseWith: (test: SampleTestDto, outcome: SampleTestOutcome) => void;
  onReopen: (test: SampleTestDto) => void;
}> = ({ test, isReopening, onSchedule, onCloseWith, onReopen }) => {
  const subject = `${test.partner.name}, ${test.product.code}`;

  if (test.status !== 'Testing')
    return (
      <div className="flex justify-end">
        <Button
          variant="ghost"
          size="sm"
          className={ICON_BUTTON}
          data-action="reopen"
          aria-label={`Вернуть в тестирование: ${subject}`}
          title="Вернуть в тестирование - если итог поставлен по ошибке"
          disabled={isReopening}
          onClick={() => onReopen(test)}
        >
          <RotateCcw aria-hidden />
        </Button>
      </div>
    );

  return (
    <div className="flex justify-end gap-0.5">
      <Button
        variant="ghost"
        size="sm"
        className={ICON_BUTTON}
        data-action="schedule"
        aria-label={`Перенести тест: ${subject}`}
        title="Перенести дату теста"
        onClick={() => onSchedule(test)}
      >
        <CalendarClock aria-hidden />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className={cn(ICON_BUTTON, 'hover:text-st-completed')}
        data-action="succeed"
        aria-label={`Одобрено: ${subject}`}
        title="Одобрено"
        onClick={() => onCloseWith(test, 'Succeeded')}
      >
        <Check aria-hidden />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className={cn(ICON_BUTTON, 'hover:text-st-returned')}
        data-action="fail"
        aria-label={`Не подошло: ${subject}`}
        title="Не подошло"
        onClick={() => onCloseWith(test, 'Failed')}
      >
        <X aria-hidden />
      </Button>
    </div>
  );
};
